import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppShell from "./components/AppShell";
import PublicLayout from "./components/PublicLayout";
import { Spinner } from "./components/kit";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import Admin from "./pages/app/Admin";
import ApiKeys from "./pages/app/ApiKeys";
import Billing from "./pages/app/Billing";
import Dashboard from "./pages/app/Dashboard";
import Domains from "./pages/app/Domains";
import Messages from "./pages/app/Messages";
import Files from "./pages/app/Files";
import Templates from "./pages/app/Templates";
import Webhooks from "./pages/app/Webhooks";
import Campaigns from "./pages/app/Campaigns";
import CampaignPage from "./pages/app/CampaignEditor";
import Audiences from "./pages/app/Audiences";
import { Guide, Guides } from "./pages/public/Guides";
import { Blog, BlogPost } from "./pages/public/Blog";
import { AppDocs, AuditLog, SettingsPage, Suppressions, Team, Usage } from "./pages/app/Workspace";
import { AuthCallback, ForgotPassword, Login, Register, ResetPassword, Unsubscribe, VerifyEmail } from "./pages/public/Auth";
import { PublicDocs, Pricing, Support } from "./pages/public/Info";
import SmtpDocs from "./pages/public/SmtpDocs";
import Landing from "./pages/public/Landing";

function Protected({ children, admin }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#070709]"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !user.is_platform_admin) return <Navigate to="/app" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/docs" element={<PublicDocs />} />
        <Route path="/docs/smtp" element={<SmtpDocs />} />
        <Route path="/support" element={<Support />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/guides/:slug" element={<Guide />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="messages" element={<Messages />} />
        <Route path="templates" element={<Templates />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/:id" element={<CampaignPage />} />
        <Route path="audiences" element={<Audiences />} />
        <Route path="files" element={<Files />} />
        <Route path="domains" element={<Domains />} />
        <Route path="suppressions" element={<Suppressions />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="webhooks" element={<Webhooks />} />
        <Route path="docs" element={<AppDocs />} />
        <Route path="usage" element={<Usage />} />
        <Route path="team" element={<Team />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="billing" element={<Billing />} />
        <Route path="admin" element={<Protected admin><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter><Router /></BrowserRouter>
        <Toaster theme="dark" richColors position="top-right" />
      </AuthProvider>
    </I18nProvider>
  );
}
